import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { getCurrentDevise } from '../utils/currency';
import { useAuth } from './AuthContext';
import { User } from '../types/user';

export type ModuleType = 'dashboard' | 'caisse' | 'stock' | 'inventaire' | 'promotions' | 'admin' | 'supervision' | 'statistics' | 'alertes';

export interface Article {
  id: string;
  code: string;
  codeBarres: string;
  nom: string;
  stock: number;
  prix: number;
  categorie: string;
  stockMin: number;
  unite?: string;
  quantiteDivisible?: boolean;
}

export interface PanierItem {
  article: Article;
  quantite: number;
  remise: number;
  montantRemise: number;
  prixFinal: number;
  codeBarres: string;
}

export type ModePayement = 'especes' | 'carte' | 'mobile' | 'cheque' | 'virement' | 'credit';

export interface Vente {
  id: string;
  date: string;
  articles: {
    article: Article;
    quantite: number;
    prix: number;
  }[];
  total: number;
  modePayement: ModePayement;
  statut: 'termine';
  caisseId: string;
  utilisateurId: string;
  societe: number;
  magasin: number;
}

// Types pour les promotions
export interface Promotion {
  id: string;
  code: string;
  type: 'pourcentage' | 'montant' | 'article_gratuit';
  valeur: number;
  description: string;
  dateDebut: string;
  dateFin: string;
  utilisationsMax: number;
  utilisationsRestantes: number;
  conditionMinMontant?: number;
  actif: boolean;
}

export interface CarteCadeau {
  id: string;
  code: string;
  montantInitial: number;
  solde: number;
  dateCreation: string;
  dateExpiration: string;
  clientId?: string;
  actif: boolean;
}

export interface BonReduction {
  id: string;
  code: string;
  montant: number;
  dateCreation: string;
  dateExpiration: string;
  utilise: boolean;
  dateUtilisation?: string;
}

interface AppContextType {
  activeModule: ModuleType;
  setActiveModule: (module: ModuleType) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  articles: Article[];
  setArticles: (articles: Article[]) => void;
  panier: PanierItem[];
  setPanier: (panier: PanierItem[]) => void;
  ventes: Vente[];
  setVentes: (ventes: Vente[]) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  
  // Nouvelles propriétés pour les promotions
  promotions: Promotion[];
  setPromotions: (promotions: Promotion[]) => void;
  cartesCadeaux: CarteCadeau[];
  setCartesCadeaux: (cartes: CarteCadeau[]) => void;
  bonsReduction: BonReduction[];
  setBonsReduction: (bons: BonReduction[]) => void;
  
  // Fonctions utilitaires pour les promotions
  appliquerPromotion: (code: string, montantPanier: number) => { 
    type: string; 
    valeur: number; 
    message: string; 
  } | null;
  appliquerCarteCadeau: (code: string) => { 
    montant: number; 
    message: string; 
  } | null;
  appliquerBonReduction: (code: string) => { 
    montant: number; 
    message: string; 
  } | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeModule, setActiveModule] = useState<ModuleType>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [user, setUser] = useState<User | null>(null);
  
  // États pour les promotions
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [cartesCadeaux, setCartesCadeaux] = useState<CarteCadeau[]>([]);
  const [bonsReduction, setBonsReduction] = useState<BonReduction[]>([]);

  const { user: authUser, isAuthenticated } = useAuth();

  // Synchronisation de l'utilisateur
  useEffect(() => {
    if (isAuthenticated && authUser) {
      setUser({
        ...authUser,
        prenom: authUser.prenom || ''
      } as User);
    } else {
      setUser(null);
    }
  }, [authUser, isAuthenticated]);

  // Articles par défaut
  const [articles, setArticles] = useState<Article[]>([
    {
      id: '1',
      code: 'ART001',
      nom: 'Ordinateur Portable',
      stock: 10,
      codeBarres: '0120200',
      prix: 450000,
      categorie: 'Informatique',
      stockMin: 2
    },
    {
      id: '2',
      code: 'ART002',
      nom: 'Souris USB',
      stock: 25,
      prix: 15000,
      codeBarres: '012022030',
      categorie: 'Informatique',
      stockMin: 5
    },
    {
      id: '3',
      code: 'ART003',
      nom: 'Clavier Mécanique',
      stock: 15,
      prix: 35000,
      codeBarres: '012022031',
      categorie: 'Informatique',
      stockMin: 3
    },
    {
      id: '4',
      code: 'ART004',
      nom: 'Ecran 24"',
      stock: 8,
      codeBarres: '012022032',
      prix: 120000,
      categorie: 'Informatique',
      stockMin: 2
    },
    {
      id: '5',
      code: 'ART005',
      nom: 'Casque Audio',
      stock: 20,
      codeBarres: '0120220325',
      prix: 25000,
      categorie: 'Audio',
      stockMin: 4
    }
  ]);

  const [panier, setPanier] = useState<PanierItem[]>([]);
  const [ventes, setVentes] = useState<Vente[]>([]);

  // Fonctions pour appliquer les promotions
  const appliquerPromotion = (code: string, montantPanier: number) => {
    const promotion = promotions.find(p => 
      p.code === code && 
      p.actif && 
      new Date(p.dateFin) >= new Date() &&
      p.utilisationsRestantes > 0
    );

    if (!promotion) {
      return null;
    }

    // Vérifier le montant minimum
    if (promotion.conditionMinMontant && montantPanier < promotion.conditionMinMontant) {
      return null;
    }

    let valeur = 0;
    let message = '';

    if (promotion.type === 'pourcentage') {
      valeur = (montantPanier * promotion.valeur) / 100;
      message = `Promotion ${promotion.valeur}% appliquée`;
    } else if (promotion.type === 'montant') {
      valeur = promotion.valeur;
      message = `Réduction de ${promotion.valeur} ${getCurrentDevise()} appliquée`;
    } else if (promotion.type === 'article_gratuit') {
      valeur = 0; // À gérer différemment
      message = 'Article gratuit offert';
    }

    return { type: promotion.type, valeur, message };
  };

  const appliquerCarteCadeau = (code: string) => {
    const carte = cartesCadeaux.find(c => 
      c.code === code && 
      c.actif && 
      c.solde > 0 &&
      (!c.dateExpiration || new Date(c.dateExpiration) >= new Date())
    );

    if (!carte) {
      return null;
    }

    return { 
      montant: carte.solde, 
      message: `Carte cadeau valide - Solde: ${carte.solde} CFA` 
    };
  };

  const appliquerBonReduction = (code: string) => {
    const bon = bonsReduction.find(b => 
      b.code === code && 
      !b.utilise &&
      (!b.dateExpiration || new Date(b.dateExpiration) >= new Date())
    );

    if (!bon) {
      return null;
    }

    return { 
      montant: bon.montant, 
      message: `Bon de réduction valide - Montant: ${bon.montant} CFA` 
    };
  };

  // Charger les promotions au démarrage (simulation)
  useEffect(() => {
    if (isAuthenticated) {
      // Simuler le chargement des promotions
      const promotionsExemple: Promotion[] = [
        {
          id: '1',
          code: 'ETE2024',
          type: 'pourcentage',
          valeur: 10,
          description: 'Promotion été 2024',
          dateDebut: '2024-06-01',
          dateFin: '2024-08-31',
          utilisationsMax: 100,
          utilisationsRestantes: 85,
          conditionMinMontant: 50000,
          actif: true
        }
      ];

      const cartesExemple: CarteCadeau[] = [
        {
          id: '1',
          code: 'CARTE-1234-5678',
          montantInitial: 50000,
          solde: 25000,
          dateCreation: '2024-01-01',
          dateExpiration: '2024-12-31',
          actif: true
        }
      ];

      const bonsExemple: BonReduction[] = [
        {
          id: '1',
          code: 'BON2024',
          montant: 15000,
          dateCreation: '2024-01-01',
          dateExpiration: '2024-12-31',
          utilise: false
        }
      ];

      setPromotions(promotionsExemple);
      setCartesCadeaux(cartesExemple);
      setBonsReduction(bonsExemple);
    }
  }, [isAuthenticated]);

  return (
    <AppContext.Provider value={{
      activeModule,
      setActiveModule,
      sidebarOpen,
      setSidebarOpen,
      articles,
      setArticles,
      panier,
      setPanier,
      ventes,
      setVentes,
      user,
      setUser,
      
      // Promotions
      promotions,
      setPromotions,
      cartesCadeaux,
      setCartesCadeaux,
      bonsReduction,
      setBonsReduction,
      
      // Fonctions
      appliquerPromotion,
      appliquerCarteCadeau,
      appliquerBonReduction
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};